import { manholeRatesByDiameter } from "../mock";
import { Manhole } from "../types/tender.types";

export interface ManholeRateItem {
  item: number; // Serial number
  description: string; // Rate item name
  qty: number; // Quantity of same diameter/depth combinations
  unit: string; // Always "nr"
  rate: number; // Rate per unit
  amount: number; // Rate * Qty
  diameter: string; // For grouping
  exactMatch: boolean;
}

export interface DiameterGroup {
  diameter: string;
  items: ManholeRateItem[];
  subtotal: number;
}

/**
 * Calculate rate for a single manhole based on diameter and depth category
 */
const calculateSingleManholeRate = (manhole: Manhole) => {
  const diameter = manhole.diameter;
  const depthCategory = manhole.depthCategory;

  // Get rates for this diameter
  const ratesForDiameter =
    manholeRatesByDiameter[diameter as keyof typeof manholeRatesByDiameter];

  if (!ratesForDiameter) {
    return {
      rate: 0,
      description: `No rates available for ${diameter}`,
      exactMatch: false,
    };
  }

  // Convert depth category to string format (e.g., 1.25 -> "1.25m")
  const depthStr = `${depthCategory.toFixed(2)}m`;

  // Try to find exact match first
  const exactMatch = ratesForDiameter.find((rate) => rate.depth === depthStr);
  if (exactMatch) {
    return {
      rate: exactMatch.rate,
      description: exactMatch.name,
      exactMatch: true,
    };
  }

  // If no exact match, find closest depth
  const availableDepths = ratesForDiameter.map((rate) =>
    parseFloat(rate.depth.replace("m", ""))
  );
  const closestDepth = availableDepths.reduce((prev, curr) =>
    Math.abs(curr - depthCategory) < Math.abs(prev - depthCategory)
      ? curr
      : prev
  );

  const closestRate = ratesForDiameter.find(
    (rate) => parseFloat(rate.depth.replace("m", "")) === closestDepth
  );

  if (closestRate) {
    return {
      rate: closestRate.rate,
      description: `${closestRate.name} (closest match for ${depthStr})`,
      exactMatch: false,
    };
  }

  return {
    rate: 0,
    description: `No suitable rate found for ${diameter} at ${depthStr}`,
    exactMatch: false,
  };
};

/**
 * Calculate all available rates showing only quantities where manholes exist
 */
export const calculateAllAvailableRates = (
  manholes: Manhole[]
): DiameterGroup[] => {
  // Count manholes by diameter and depth combination
  const manholeCount: { [key: string]: number } = {};

  manholes.forEach((manhole) => {
    const depthStr = `${manhole.depthCategory.toFixed(2)}m`;
    const key = `${manhole.diameter}_${depthStr}`;
    manholeCount[key] = (manholeCount[key] || 0) + 1;
  });

  // Create items for all available rates
  const diameterGroups: DiameterGroup[] = [];
  let itemCounter = 1;

  Object.entries(manholeRatesByDiameter).forEach(([diameter, rates]) => {
    const items: ManholeRateItem[] = [];

    rates.forEach((rate) => {
      const key = `${diameter}_${rate.depth}`;
      const qty = manholeCount[key] || 0;
      const amount = qty > 0 ? rate.rate * qty : 0;

      items.push({
        item: itemCounter++,
        description: rate.name,
        qty: qty,
        unit: qty > 0 ? "Nr" : "",
        rate: rate.rate,
        amount: amount,
        diameter: diameter,
        exactMatch: true, // These are exact matches from the rate library
      });
    });

    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);

    diameterGroups.push({
      diameter,
      items,
      subtotal,
    });
  });

  return diameterGroups;
};

/**
 * Calculate grouped manhole rates with quantities (legacy function for compatibility)
 */
export const calculateGroupedManholeRates = (
  manholes: Manhole[]
): DiameterGroup[] => {
  return calculateAllAvailableRates(manholes);
};

/**
 * Get total value of all grouped rates
 */
export const getTotalGroupedValue = (groups: DiameterGroup[]): number => {
  return groups.reduce((total, group) => total + group.subtotal, 0);
};
